import TicketDetails from '../../../classes/api/TicketDetails';
import {Alert, Col, Row} from 'react-bootstrap';
import CardComponent from '../../../common/components/CardComponent';
import React, {ChangeEvent, useRef, useState} from 'react';
import Button from 'react-bootstrap/Button';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {FormikProps, FormikTouched, useFormik} from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {useClientName} from '../../../hooks/useClientName';
import {Answer} from '../../../interfaces/answer';
import {answerTicket} from '../../../api/ticket';
import TicketAnswer from '../../../classes/api/TicketAnswer';
import {MAX_UPLOAD_FILE_SIZE} from '../../admin/documents/api';
import {useMutation} from 'react-query';
import {DateTime} from 'luxon';
import {
  getStatusColor,
  getStatusTextColor,
  getStatusLabel,
} from '../../../../Domains/Ticket/Helpers/stateHelper';
import statusBadgeStyles from '../../../../Domains/Ticket/Components/TicketStatusBadge.module.css';
import './Ticket.scss';

/** Parses a French date string ("15/06/2026 à 14:30:00" or "15/06/2026 14:30:00") into a DateTime, or null. */
const parseFrenchDate = (value: string | null): DateTime | null => {
  if (!value) return null;
  const cleaned = value.replace(' à ', ' ');
  const dt = DateTime.fromFormat(cleaned, 'dd/MM/yyyy HH:mm:ss', {locale: 'fr'});
  return dt.isValid ? dt : null;
};

/** Returns a localized relative time (e.g. "il y a 3 jours") for a French date string. */
const formatRelativeDate = (value: string | null): string | null => {
  const dt = parseFrenchDate(value);
  return dt ? dt.toRelative() : null;
};

/** Returns a long localized date (e.g. "23 mai 2026 · 09:14") for a French date string. */
const formatDateLong = (value: string | null): string | null => {
  const dt = parseFrenchDate(value);
  return dt ? dt.setLocale('fr').toFormat("d LLLL yyyy '·' HH:mm") : null;
};

/** Returns a relative time + clock (e.g. "il y a 2 jours · 14:32") for a French date string. */
const formatRelativeDateTime = (value: string | null): string | null => {
  const dt = parseFrenchDate(value);
  if (!dt) return null;
  return `${dt.toRelative()} · ${dt.toFormat('HH:mm')}`;
};

/** Extracts a readable display name from a comment author ("Pierre Laurent <x@y>" or "x@y"). */
const getCommentAuthorName = (raw: string | null, isSupport: boolean): string => {
  if (!raw || raw.trim() === '') return isSupport ? 'Support Celeste' : 'Vous';
  const name = (raw.includes('<') ? raw.split('<')[0] : raw).trim();
  if (name && !name.includes('@')) return name;
  if (isSupport) return 'Support Celeste';
  return name.split('@')[0];
};

/** Builds up-to-two-letter initials from a display name. */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Returns the relative time of the most recent comment, falling back to the opening date. */
const getLastActivityRelative = (ticket: TicketDetails): string | null => {
  const dates = ticket
    .getComments()
    .map((comment) => parseFrenchDate(comment.getDate()))
    .filter((dt): dt is DateTime => dt !== null);
  if (dates.length === 0) {
    return formatRelativeDate(ticket.getOpeningDate());
  }
  const latest = dates.reduce((a, b) => (a > b ? a : b));
  return latest.toRelative();
};

interface TicketProps {
  ticket: TicketDetails | undefined;
  refetchTicket: () => void;
}

/** Displays a support ticket (header, comments, details) or an error message if unavailable. */
const Ticket = ({ticket, refetchTicket}: TicketProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [showOlderComments, setShowOlderComments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const author = useClientName();
  const title = ticket?.getTitle();
  const initialAnswerValues: Answer = {
    title: typeof title === 'string' ? title : '',
    message: '',
    file: undefined,
  };
  const InitialAnswerTouched: FormikTouched<Answer> = {
    title: false,
    message: false,
    file: false,
  };
  const answerValidationRules: Yup.SchemaOf<Answer> = Yup.object().shape({
    title: Yup.mixed().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    message: Yup.mixed().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    file: Yup.mixed()
      .notRequired()
      .test(
        'fileSize',
        intl.formatMessage({id: 'TOO.BIG'}) + `${MAX_UPLOAD_FILE_SIZE / 1000000}MB`,
        (value) => {
          if (!value) return true;
          return value.size <= MAX_UPLOAD_FILE_SIZE;
        }
      ),
  });

  const answerMutation = useMutation(answerTicket, {
    onSuccess: () => {
      formik.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(intl.formatMessage({id: 'TICKET.ANSWER.SENT'}));
      refetchTicket();
    },
    onError: () => {
      toast.error(intl.formatMessage({id: 'TICKET.ANSWER.ERROR'}));
    },
  });

  const formik: FormikProps<Answer> = useFormik({
    initialValues: initialAnswerValues,
    initialTouched: InitialAnswerTouched,
    validationSchema: answerValidationRules,
    validateOnChange: true,
    onSubmit: async (values: Answer) => {
      try {
        let ticketAnswer = new TicketAnswer(
          values.title,
          values.message,
          ticket?.getCustomerId() === undefined ? '' : ticket?.getCustomerId()! + '',
          ticket?.getTicketNumber() === undefined ? '' : ticket?.getTicketNumber()! + '',
          author,
          []
        );
        if (values.file) {
          ticketAnswer.addAttachment(values.file);
        }
        answerMutation.mutate(ticketAnswer);
        return;
      } catch (error) {
        toast.error(intl.formatMessage({id: 'TICKET.ANSWER.ERROR'}));
      }
    },
    onReset: () => {},
  });

  const fileOnChange = async (e: ChangeEvent) => {
    const target = e.target as HTMLInputElement;
    if (!target.files || target.files.length <= 0) {
      return;
    }
    const file: File = (target.files as FileList)[0];
    await formik.setFieldValue('file', file);
    await formik.setTouched({...formik.touched, file: true}, true);
  };

  /** Pré-remplit le titre (toujours celui du ticket) puis soumet la réponse. */
  const handleSendAnswer = async () => {
    if (ticket?.getTitle()) {
      await formik.setFieldValue('title', ticket.getTitle());
    }
    await formik.submitForm();
  };

  function linkifyText(text: string): string {
    const urlPattern =
      /\b((?:https?:\/\/|www\.)[^\s<>"']+)|\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,})/g;

    return text.replace(urlPattern, (match, url, email) => {
      if (email) {
        return `<a href="mailto:${email}">${email}</a>`;
      }
      let href = url;
      if (href.startsWith('www.')) href = 'https://' + href;
      const safeText = match.replace(/"/g, '&quot;');
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
    });
  }

  /** Carte "État du ticket", affichée avant les commentaires sur mobile et dans la colonne de droite sur desktop. */
  const etatDuTicketCard = ticket && (
    <div className="card mb-5 mb-xl-5 mt-0">
      <div className="card-header">
        <div className="card-title m-0 d-flex align-items-center justify-content-between w-100 gap-3">
          <h3 className="fs-3 fw-semibold m-0">État du ticket</h3>
          <span
            className={statusBadgeStyles.statusBadge}
            style={{
              backgroundColor: getStatusColor(ticket.getStatus()),
              color: getStatusTextColor(getStatusColor(ticket.getStatus())),
              fontSize: '0.8em',
              padding: '0.15em 0.7em',
            }}
          >
            {getStatusLabel(ticket.getStatus(), intl)}
          </span>
        </div>
      </div>
    </div>
  );

  /** Carte "Détails", affichée avant les commentaires sur mobile et dans la colonne de droite sur desktop. */
  const detailsCard = ticket && (
    <div className="card mb-5 mb-xl-10 mt-0">
      <div className="card-header">
        <div className="card-title m-0">
          <h3 className="fs-3 fw-semibold">Détails</h3>
        </div>
      </div>
      <div className="card-body px-9 py-6">
        <div className="d-flex flex-column gap-4">
          <div className="d-flex justify-content-between align-items-start gap-3">
            <span className="text-muted flex-shrink-0">Numéro</span>
            <span className="fw-semibold text-gray-800 text-end">{ticket.getTicketNumber()}</span>
          </div>
          {formatDateLong(ticket.getOpeningDate()) && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">Créé le</span>
              <span className="fw-semibold text-gray-800 text-end">
                {formatDateLong(ticket.getOpeningDate())}
              </span>
            </div>
          )}
          {getLastActivityRelative(ticket) && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">Mise à jour</span>
              <span className="fw-semibold text-gray-800 text-end">
                {getLastActivityRelative(ticket)}
              </span>
            </div>
          )}
          {ticket.getTitle() && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">Sujet</span>
              <span className="fw-semibold text-gray-800 text-end text-break">
                {ticket.getTitle()}
              </span>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-start gap-3">
            <span className="text-muted flex-shrink-0">Compte</span>
            <span className="fw-semibold text-gray-800 text-end">
              {ticket.getCustomerId()}
              {ticket.hasCustomerName() ? ' - ' + ticket.getCustomerName() : ''}
            </span>
          </div>
          {(ticket.getSiteId() || ticket.getSiteName()) && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">Site</span>
              <span className="fw-semibold text-gray-800 text-end">
                {ticket.getSiteId()} - {ticket.getSiteName()}
              </span>
            </div>
          )}
          {ticket.hasLink() && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">Prestation</span>
              <span className="fw-semibold text-gray-800 text-end text-break">
                {ticket.getLink()} - {ticket.getLinkName()}
              </span>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-start gap-3">
            <span className="text-muted flex-shrink-0">Auteur</span>
            <span className="fw-semibold text-gray-800 text-end text-break">
              {ticket.hasAuthor() ? ticket.getAuthor() : 'N/A'}
            </span>
          </div>
          {ticket.getOrigin() === 'OTRS' && (
            <div className="d-flex justify-content-between align-items-start gap-3">
              <span className="text-muted flex-shrink-0">GTR</span>
              <span className="fw-semibold text-gray-800 text-end">
                {ticket.hasSla() ? ticket.getSla() : '-'}
              </span>
            </div>
          )}
          {ticket.hasContacts() && (
            <div className="d-flex flex-column gap-2">
              <span className="text-muted">Contacts rattachés au dossier</span>
              <div className="d-flex flex-column gap-2">
                {ticket.getContacts().map((contact) => (
                  <div className="d-flex flex-column" key={contact.getFullName()}>
                    <span className="fw-semibold text-gray-800">{contact.getFullName()}</span>
                    <span className="text-muted fs-7">
                      {contact.getEmail()} / {contact.getMobile()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ticket.hasDescription() && (
            <div className="d-flex flex-column gap-2">
              <span className="text-muted">Description</span>
              <span
                className="fw-semibold fs-7 text-gray-800 text-justify"
                dangerouslySetInnerHTML={{
                  __html: ticket.getDescription()!.replace(/(?:\r\n|\r|\n)/g, '<br>'),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /** Carte "Prestation liée", affichée avant les commentaires sur mobile et dans la colonne de droite sur desktop. */
  const prestationCard = ticket && ticket.hasLink() && (
    <div className="card mb-5 mb-xl-10 mt-0">
      <div className="card-body px-9 py-6">
        <span
          className="text-muted fs-8 fw-semibold text-uppercase d-block mb-3"
          style={{letterSpacing: '0.05em'}}
        >
          Prestation liée
        </span>
        <div className="fw-bold fs-5 text-gray-900 mb-1">
          {ticket.getLinkName() || ticket.getLink()}
        </div>
        {ticket.getSubType() && <div className="text-muted fs-7 mb-4">{ticket.getSubType()}</div>}
        <div
          className="d-flex align-items-start gap-3 p-4 mb-4 rounded"
          style={{backgroundColor: '#f9f9f9', border: '1px solid #eff2f5'}}
        >
          <i className="fa fa-building text-gray-500 mt-1" style={{fontSize: '20px'}}></i>
          <div className="d-flex flex-column">
            {ticket.getSiteName() && (
              <span className="fw-semibold text-gray-800">{ticket.getSiteName()}</span>
            )}
            {ticket.getLink() && <span className="text-muted fs-7">{ticket.getLink()}</span>}
            {ticket.hasCustomerName() && (
              <span className="text-muted fs-7">{ticket.getCustomerName()}</span>
            )}
          </div>
        </div>
        <Button
          variant="link"
          className="p-0 fw-semibold text-decoration-none"
          onClick={() => navigate(`/services/connexion/liens/${ticket.getLink()}`)}
        >
          → Voir la prestation dans mes services
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {typeof ticket === 'undefined' && (
        <Row>
          <Col>
            <Alert variant="warning">
              Une erreur est survenue lors de la récupération des informations du ticket. Veuillez
              réessayer ultérieurement.
            </Alert>
            <Button
              variant={'primary'}
              onClick={() => {
                navigate(`/support-list`, {state: {}});
              }}
            >
              Retour
            </Button>
          </Col>
        </Row>
      )}
      {typeof ticket !== 'undefined' && (
        <Row>
          <Col md={9}>
            <div className="card mb-5 mb-xl-5 mt-0">
              <div className="card-body p-8">
                <div className="d-flex flex-column">
                  <h3 className="fw-bold text-dark mb-3">
                    {ticket.getTitle()}
                    {ticket.getSiteName() ? ' - ' + ticket.getSiteName() : ''}
                  </h3>
                  
                  <div className="text-muted fs-7 d-flex flex-wrap align-items-center gap-2">
                    {formatRelativeDate(ticket.getOpeningDate()) && (
                      <>
                        <span>Ouvert {formatRelativeDate(ticket.getOpeningDate())}</span>
                        <span>-</span>
                      </>
                    )}
                    <span>
                      {ticket.getComments().length} message
                      {ticket.getComments().length > 1 ? 's' : ''}
                    </span>
                    {getLastActivityRelative(ticket) && (
                      <>
                        <span>-</span>
                        <span>Dernière activité {getLastActivityRelative(ticket)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-md-none">
              {etatDuTicketCard}
              {detailsCard}
              {prestationCard}
            </div>

            <CardComponent title={'Commentaires'} customClass="mt-0">
              {ticket && ticket.hasComments() && ticket.getComments().length > 0 ? (
                (() => {
                  // Fil de discussion en ordre chronologique (le plus ancien en haut).
                  const sortedComments = [...ticket.getComments()].sort((a, b) => {
                    const dateA = parseFrenchDate(a.getDate());
                    const dateB = parseFrenchDate(b.getDate());
                    return (dateA?.toMillis() ?? 0) - (dateB?.toMillis() ?? 0);
                  });

                  // On replie les plus anciens et on garde les VISIBLE_COUNT derniers visibles.
                  const VISIBLE_COUNT = 2;
                  const olderCount = Math.max(0, sortedComments.length - VISIBLE_COUNT);
                  const visibleComments =
                    showOlderComments || olderCount === 0
                      ? sortedComments
                      : sortedComments.slice(olderCount);

                  return (
                    <div className="ticketThread">
                      {olderCount > 0 && !showOlderComments && (
                        <button
                          type="button"
                          className="ticketThread__showOlder"
                          onClick={() => setShowOlderComments(true)}
                        >
                          <i className="fa fa-arrow-up me-2"></i>
                          Afficher {olderCount} message{olderCount > 1 ? 's' : ''} plus ancien
                          {olderCount > 1 ? 's' : ''}
                        </button>
                      )}

                      {visibleComments.map((comment) => {
                        const isSupport = comment.isFromSupport();
                        const authorName = getCommentAuthorName(comment.getAuthor(), isSupport);
                        return (
                          <div
                            className={`ticketThread__message${
                              isSupport ? ' ticketThread__message--support' : ''
                            }`}
                            key={comment.getId()}
                          >
                            <div className="ticketThread__avatar">{getInitials(authorName)}</div>
                            <div className="ticketThread__bubble">
                              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="fw-bold text-gray-900">{authorName}</span>
                                  <span className="ticketThread__badge">
                                    {isSupport ? 'Conseiller Celeste' : 'Vous'}
                                  </span>
                                </div>
                                <span className="ticketThread__time">
                                  {formatRelativeDateTime(comment.getDate()) ?? comment.getDate()}
                                </span>
                              </div>
                              <div
                                className="ticketThread__body"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    comment.getMessage() === null
                                      ? ''
                                      : linkifyText(
                                          comment.getMessage()!.replace(/(?:\r\n|\r|\n)/g, '<br>')
                                        ),
                                }}
                              />
                              {comment.getAttachments() && (
                                <div className="ticketThread__attachment">
                                  <i className="fa fa-paperclip"></i>
                                  <span>{comment.getAttachments()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {!['Fermé avec succés', 'Fermé sans succés', 'Supprimé'].includes(
                        ticket.getStatus() ?? ''
                      ) && <div className="ticketThread__composer">
                        <textarea
                          className="ticketThread__composerInput"
                          name="message"
                          rows={3}
                          placeholder="Tapez votre réponse…"
                          value={formik.values.message}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.values.file && (
                          <div className="ticketThread__composerFile">
                            <i className="fa fa-paperclip"></i>
                            <span>{(formik.values.file as File).name}</span>
                            <button
                              type="button"
                              className="ticketThread__composerFileRemove"
                              aria-label="Retirer la pièce jointe"
                              onClick={() => formik.setFieldValue('file', undefined)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                        )}
                        <div className="ticketThread__composerActions">
                          <button
                            type="button"
                            className="ticketThread__composerLink"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <i className="fa fa-paperclip me-2"></i>
                            Joindre
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="d-none"
                            onChange={fileOnChange}
                          />
                          <Button
                            variant={
                              !formik.values.message?.trim() || formik.isSubmitting
                                ? 'secondary'
                                : 'primary'
                            }
                            size="sm"
                            disabled={!formik.values.message?.trim() || formik.isSubmitting}
                            onClick={handleSendAnswer}
                          >
                            Envoyer
                            <i className="fa fa-paper-plane ms-2"></i>
                          </Button>
                        </div>
                      </div>}
                    </div>
                  );
                })()
              ) : (
                <></>
              )}
            </CardComponent>
          </Col>

          <Col md={3}>
            <div className="d-none d-md-block">
              {etatDuTicketCard}
              {detailsCard}
              {prestationCard}
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default Ticket;
